/**
 * THE COMP EVENT PAGE'S ABOUT DATA (Bobbie, 2026-09-02, looking at the
 * Nationals event page's About tab): "I need a teachers section here that
 * directors and managers can edit and add things. we need to be able to add
 * photos and links a smart editor for all of these.... again similar to
 * facebook events."
 *
 * Lives at `eventV2.about` on the countdownEvents document, which
 * shared/firestore.rules validates as `is map` only (Stage B, 2026-08-20),
 * so every field here is a CLIENT change - see countdownEvents.ts's own
 * comment on scheduleInfo/aboutText/hotelInfo/travelInfo for why that is
 * deliberate and costs zero rules bytes. Writers are Director and Manager,
 * gated the same way as every other countdownEvents edit (Bobbie,
 * 2026-09-01: "managers and directors can do all of the things").
 *
 * The named facts (TASK-QUEUE.md, "COMP SHARE PAGE... per-comp DATA she or
 * the director must enter once the editor lands"): livestream details and
 * pass cost, parking, tickets, where studio families sit, the no-photography
 * flag, a cover photo. This is that editor for every one of those EXCEPT the
 * cover photo: the coordinator's brief for this build says not to touch the
 * page header ("cover, date badge, name") because another lane owns it, and
 * the event page's cover photo lives in that header band
 * (CompetitionEvent.tsx), not inside the About tab. So `about` carries no
 * cover-photo field at all - adding one here with nothing rendering it would
 * be dead scope racing whichever lane actually wires the header's photo.
 *
 * ONE GAP LEFT HONEST ON PURPOSE: a section's own "photos grid" (the
 * Teachers section and any Director-added section) has no working "Add
 * photos" control yet. Every other attachment lane in this app writes
 * through the authenticated upload gateway's consume step, which owns
 * exactly one field per target and proves the object it links to was really
 * uploaded by that caller (see attachmentUpload.mjs's countdownEventPanel
 * branch). A section's photos are a DYNAMIC, Director-named list with no
 * fixed key, so giving it the same non-forgeable guarantee needs a new
 * server-owned array-of-photos consume lane (attachmentUpload.mjs +
 * messageAttachmentWrite.mjs + a matching rules guard generalizing
 * keepsPanelPhotos()), not a few lines. Shipping a client-writable photo URL
 * straight into eventV2.about instead would be the one place this feature
 * breaks the app's own attachment-trust pattern, so it stays undone rather
 * than half right. The `photos` field below is modeled and normalized now so
 * a follow-up PR can wire the upload without a second migration; the editor
 * simply never shows an "Add photos" button for a section yet.
 */

export const EVENT_ABOUT_SECTION_TITLE_MAX = 80;
export const EVENT_ABOUT_SECTION_BODY_MAX = 6000;
export const EVENT_ABOUT_LINK_LABEL_MAX = 60;
export const EVENT_ABOUT_LINK_URL_MAX = 500;
export const EVENT_ABOUT_MAX_SECTIONS = 12;
export const EVENT_ABOUT_MAX_LINKS_PER_SECTION = 8;
export const EVENT_ABOUT_MAX_PHOTOS_PER_SECTION = 8;
export const EVENT_ABOUT_FACT_MAX = 300;
export const EVENT_ABOUT_PASS_COST_MAX = 100;
export const EVENT_ABOUT_TICKETS_LABEL_MAX = 60;
export const EVENT_ABOUT_DESCRIPTION_MAX = 4000;
export const EVENT_ABOUT_DESCRIPTION_PREVIEW = 320;

/** The Teachers section is a convention, not a schema field: the editor
 * seeds one with this title when a Director opens Edit and none exists yet,
 * so the ask ("a teachers section") is always one tap away rather than
 * something a Director has to remember to create. Nothing in storage or the
 * read view treats a section with this exact title specially. */
export const EVENT_ABOUT_TEACHERS_SECTION_TITLE = 'Teachers';

export interface EventAboutLink {
  label: string;
  url: string;
}

/** Modeled for a future photo lane (see the file header). Not written by
 * today's editor; kept here so normalizeEventAboutSection has one real shape
 * to defend against a hand-crafted document, and so a later PR adds a writer
 * rather than a schema. */
export interface EventAboutSectionPhoto {
  id: string;
  kind: 'image';
  url: string;
  name: string;
  contentType: 'image/jpeg' | 'image/png' | 'image/gif' | 'image/webp';
  storagePath: string;
}

export interface EventAboutSection {
  id: string;
  title: string;
  body: string;
  links: EventAboutLink[];
  photos: EventAboutSectionPhoto[];
}

export interface EventAboutData {
  ticketsUrl?: string;
  ticketsLabel?: string;
  livestreamInfo?: string;
  livestreamPassCost?: string;
  parking?: string;
  familySeating?: string;
  /** When awards are, as she types it (N14: the comp share page shows it as
   * its highlighted Awards row; "A missing awards time renders as visibly
   * missing, never guessed", DECISIONS 2026-08-27). */
  awardsInfo?: string;
  noPhotography?: boolean;
  description?: string;
  sections: EventAboutSection[];
}

/** The empty-but-present shape a fresh editor draft starts from. Never
 * written as-is; validatedEventAboutInput drops every empty field before a
 * save, the same "absent means nothing to show" contract every other panel
 * on this page keeps. */
export const EMPTY_EVENT_ABOUT: EventAboutData = { sections: [] };

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

/** https only, per the brief ("Add link row... validated https"). One typed
 * mistake here is a dead button on a family's screen, not a security
 * boundary, but the studio's own links should never train a family to trust
 * an http:// prompt. */
export function isValidEventAboutUrl(value: unknown): value is string {
  if (typeof value !== 'string' || value.length > EVENT_ABOUT_LINK_URL_MAX) return false;
  try {
    const parsed = new URL(value);
    return parsed.protocol === 'https:' && Boolean(parsed.hostname);
  } catch {
    return false;
  }
}

export function isValidEventAboutLink(value: unknown): value is EventAboutLink {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Record<string, unknown>;
  return isNonEmptyString(candidate.label)
    && candidate.label.trim().length <= EVENT_ABOUT_LINK_LABEL_MAX
    && isValidEventAboutUrl(candidate.url);
}

const SECTION_ID_RE = /^[A-Za-z0-9_-]{1,40}$/;

export function genEventAboutSectionId(): string {
  return `sec-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

function normalizeEventAboutLink(value: unknown): EventAboutLink | null {
  if (!isValidEventAboutLink(value)) return null;
  return {
    label: value.label.trim().slice(0, EVENT_ABOUT_LINK_LABEL_MAX),
    url: value.url,
  };
}

function normalizeEventAboutSection(value: unknown): EventAboutSection | null {
  if (!value || typeof value !== 'object') return null;
  const raw = value as Record<string, unknown>;
  if (typeof raw.id !== 'string' || !SECTION_ID_RE.test(raw.id)) return null;
  if (!isNonEmptyString(raw.title)) return null;
  const title = raw.title.trim().slice(0, EVENT_ABOUT_SECTION_TITLE_MAX);
  const body = typeof raw.body === 'string'
    ? raw.body.trim().slice(0, EVENT_ABOUT_SECTION_BODY_MAX)
    : '';
  const links = Array.isArray(raw.links)
    ? raw.links
      .map(normalizeEventAboutLink)
      .filter((link): link is EventAboutLink => link !== null)
      .slice(0, EVENT_ABOUT_MAX_LINKS_PER_SECTION)
    : [];
  // No upload lane writes a section photo yet (see the file header), so the
  // read side never trusts one either: a hand-crafted or stale document
  // could carry an unguarded photos[].url with no safeRenderableAttachmentUrl
  // check behind it (unlike the panel-photo reader in countdownEvents.ts),
  // and the only honest answer until that lane exists is an empty gallery.
  const photos: EventAboutSectionPhoto[] = [];
  return { id: raw.id, title, body, links, photos };
}

/** Read-side normalizer: defends the render path against a hand-crafted or
 * stale document the same way normalizeEventPanelPhotos does for the older
 * panels. Never throws; drops anything it cannot use. */
export function normalizeEventAbout(value: unknown): EventAboutData | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  const raw = value as Record<string, unknown>;
  const out: EventAboutData = { sections: [] };
  if (isNonEmptyString(raw.ticketsUrl) && isValidEventAboutUrl(raw.ticketsUrl.trim())) {
    out.ticketsUrl = raw.ticketsUrl.trim();
  }
  if (isNonEmptyString(raw.ticketsLabel)) {
    out.ticketsLabel = raw.ticketsLabel.trim().slice(0, EVENT_ABOUT_TICKETS_LABEL_MAX);
  }
  if (isNonEmptyString(raw.livestreamInfo)) {
    out.livestreamInfo = raw.livestreamInfo.trim().slice(0, EVENT_ABOUT_FACT_MAX);
  }
  if (isNonEmptyString(raw.livestreamPassCost)) {
    out.livestreamPassCost = raw.livestreamPassCost.trim().slice(0, EVENT_ABOUT_PASS_COST_MAX);
  }
  if (isNonEmptyString(raw.parking)) {
    out.parking = raw.parking.trim().slice(0, EVENT_ABOUT_FACT_MAX);
  }
  if (isNonEmptyString(raw.familySeating)) {
    out.familySeating = raw.familySeating.trim().slice(0, EVENT_ABOUT_FACT_MAX);
  }
  if (isNonEmptyString(raw.awardsInfo)) {
    out.awardsInfo = raw.awardsInfo.trim().slice(0, EVENT_ABOUT_FACT_MAX);
  }
  if (raw.noPhotography === true) out.noPhotography = true;
  if (isNonEmptyString(raw.description)) {
    out.description = raw.description.trim().slice(0, EVENT_ABOUT_DESCRIPTION_MAX);
  }
  if (Array.isArray(raw.sections)) {
    out.sections = raw.sections
      .map(normalizeEventAboutSection)
      .filter((section): section is EventAboutSection => section !== null)
      .slice(0, EVENT_ABOUT_MAX_SECTIONS);
  }
  const hasAnyFact = out.ticketsUrl || out.livestreamInfo || out.livestreamPassCost
    || out.parking || out.familySeating || out.awardsInfo || out.noPhotography || out.description
    || out.sections.length > 0;
  return hasAnyFact ? out : undefined;
}

/** A section counts as real content once it has a body, a link, or a photo.
 * A bare title (the seeded Teachers placeholder before a Director types
 * anything) is an editing convenience, not something to show a family - the
 * same "a family sees a panel only once it holds something" rule every
 * other event-page panel keeps. */
export function eventAboutSectionHasContent(section: EventAboutSection): boolean {
  return Boolean(section.body) || section.links.length > 0 || section.photos.length > 0;
}

export interface EventAboutInput {
  ticketsUrl?: string;
  ticketsLabel?: string;
  livestreamInfo?: string;
  livestreamPassCost?: string;
  parking?: string;
  familySeating?: string;
  awardsInfo?: string;
  noPhotography?: boolean;
  description?: string;
  sections: Array<{
    id: string;
    title: string;
    body: string;
    links: EventAboutLink[];
  }>;
}

/**
 * Validate an editor draft into the plain object the client writes to
 * `eventV2.about`. Throws a plain-words Error on anything that will not fit,
 * the same contract validatedEventModelV2Fields keeps for its own fields, so
 * the editor's Save button can show the message directly. A section with no
 * title is dropped silently rather than refused - the editor never lets a
 * Director save one, so reaching here means a stale draft, not a mistake
 * worth blocking Save over.
 */
export function validatedEventAboutInput(input: EventAboutInput): EventAboutData {
  const out: EventAboutData = { sections: [] };

  const ticketsUrl = (input.ticketsUrl ?? '').trim();
  if (ticketsUrl) {
    if (!isValidEventAboutUrl(ticketsUrl)) {
      throw new Error('The tickets link needs to start with https://.');
    }
    out.ticketsUrl = ticketsUrl;
    const ticketsLabel = (input.ticketsLabel ?? '').trim();
    if (ticketsLabel.length > EVENT_ABOUT_TICKETS_LABEL_MAX) {
      throw new Error(`Keep the tickets label under ${EVENT_ABOUT_TICKETS_LABEL_MAX} characters.`);
    }
    if (ticketsLabel) out.ticketsLabel = ticketsLabel;
  }

  const livestreamInfo = (input.livestreamInfo ?? '').trim();
  if (livestreamInfo) {
    if (livestreamInfo.length > EVENT_ABOUT_FACT_MAX) {
      throw new Error(`Keep the livestream info under ${EVENT_ABOUT_FACT_MAX} characters.`);
    }
    out.livestreamInfo = livestreamInfo;
  }
  const livestreamPassCost = (input.livestreamPassCost ?? '').trim();
  if (livestreamPassCost) {
    if (livestreamPassCost.length > EVENT_ABOUT_PASS_COST_MAX) {
      throw new Error(`Keep the pass cost under ${EVENT_ABOUT_PASS_COST_MAX} characters.`);
    }
    out.livestreamPassCost = livestreamPassCost;
  }

  const parking = (input.parking ?? '').trim();
  if (parking) {
    if (parking.length > EVENT_ABOUT_FACT_MAX) {
      throw new Error(`Keep the parking note under ${EVENT_ABOUT_FACT_MAX} characters.`);
    }
    out.parking = parking;
  }

  const familySeating = (input.familySeating ?? '').trim();
  if (familySeating) {
    if (familySeating.length > EVENT_ABOUT_FACT_MAX) {
      throw new Error(`Keep the seating note under ${EVENT_ABOUT_FACT_MAX} characters.`);
    }
    out.familySeating = familySeating;
  }

  const awardsInfo = (input.awardsInfo ?? '').trim();
  if (awardsInfo) {
    if (awardsInfo.length > EVENT_ABOUT_FACT_MAX) {
      throw new Error(`Keep the awards note under ${EVENT_ABOUT_FACT_MAX} characters.`);
    }
    out.awardsInfo = awardsInfo;
  }

  if (input.noPhotography === true) out.noPhotography = true;

  const description = (input.description ?? '').trim();
  if (description) {
    if (description.length > EVENT_ABOUT_DESCRIPTION_MAX) {
      throw new Error(`Keep the description under ${EVENT_ABOUT_DESCRIPTION_MAX.toLocaleString('en-US')} characters.`);
    }
    out.description = description;
  }

  if (input.sections.length > EVENT_ABOUT_MAX_SECTIONS) {
    throw new Error(`Keep it to ${EVENT_ABOUT_MAX_SECTIONS} sections or fewer.`);
  }
  const sections: EventAboutSection[] = [];
  for (const raw of input.sections) {
    const title = (raw.title ?? '').trim();
    if (!title) continue; // an untitled draft section is dropped, not saved
    if (title.length > EVENT_ABOUT_SECTION_TITLE_MAX) {
      throw new Error(`Keep each section title under ${EVENT_ABOUT_SECTION_TITLE_MAX} characters.`);
    }
    if (!raw.id || !SECTION_ID_RE.test(raw.id)) {
      throw new Error('One section lost its id. Refresh and try again.');
    }
    const body = (raw.body ?? '').trim();
    if (body.length > EVENT_ABOUT_SECTION_BODY_MAX) {
      throw new Error(`Keep each section under ${EVENT_ABOUT_SECTION_BODY_MAX.toLocaleString('en-US')} characters.`);
    }
    if (raw.links.length > EVENT_ABOUT_MAX_LINKS_PER_SECTION) {
      throw new Error(`Keep it to ${EVENT_ABOUT_MAX_LINKS_PER_SECTION} links per section.`);
    }
    const links: EventAboutLink[] = [];
    for (const link of raw.links) {
      const label = (link.label ?? '').trim();
      const url = (link.url ?? '').trim();
      if (!label && !url) continue;
      if (!label || label.length > EVENT_ABOUT_LINK_LABEL_MAX) {
        throw new Error(`Every link needs a short label, under ${EVENT_ABOUT_LINK_LABEL_MAX} characters.`);
      }
      if (!isValidEventAboutUrl(url)) {
        throw new Error(`The "${label}" link needs to start with https://.`);
      }
      links.push({ label, url });
    }
    sections.push({ id: raw.id, title, body, links, photos: [] });
  }
  out.sections = sections;
  return out;
}
